/**
 * Prumo triage classifier · Wave JP-smoke-review
 *
 * Groups PrumoProposal items into four buckets so the queue UI can
 * show "Aprovar hoje" first instead of a flat anxiety-inducing list
 * of 50 items.
 *
 * Shape note: the web-layer PrumoProposal type uses `kind` (a union of
 * recognized kinds) and `tier` (1 | 2 | 3). There is NO `severity`
 * or `source` field on the API response — those live inside `payload`
 * or `risks[]` which we don't parse here. The classifier therefore
 * works exclusively off `kind` and `tier`, which are always present
 * and typed.
 *
 * Bucket priority order (top-to-bottom in the UI):
 *   approve_today  → needs an explicit decision right now
 *   blocked        → blocked by external factor; high-urgency FYI
 *   fyi            → default bucket: review/recommend cards
 *   background_health → routine system self-heal / health probes
 */

export type PrumoBucket = 'approve_today' | 'blocked' | 'fyi' | 'background_health';

export interface PrumoProposalLike {
  id: string;
  kind?: string;
  tier?: number;
  expiresAt?: string | null;
  dueAt?: string | null;
}

// Kinds that require an explicit approve/decline action (executable or
// recommendation that needs operator sign-off today).
const APPROVE_TODAY_KINDS = new Set([
  'po_approve',
  'nfe_promote_to_receipt',
  'retencao_release',
  // financial_risk_alert and claim_alert are tier-1 blocking cards —
  // operator must decide; surfacing them at the top is intentional.
  'financial_risk_alert',
  'claim_alert',
  'incident_response',
  'agent_governance',
]);

// Kinds that indicate something is blocked / needs immediate system
// attention but may not have a user-actionable approve button.
const BLOCKED_KINDS = new Set([
  'system_deploy_rollback',
  'self_heal_confidence_alert',
  'data_self_heal',
  'workflow_self_heal',
  'anomaly_resolve',
  'sla_breach',
  'inbound_email_dlq_exhausted',
  'email_delivery_alert',
]);

// Kinds that are background health / routine probes — always lowest
// priority, collapsed by default.
const BACKGROUND_HEALTH_KINDS = new Set([
  'drift_resolve',
  'rule_autopilot',
  'self_tuning',
  'resource_optimization',
  'boletim_mensal_draft',
  'monthly_close',
  'daily_brief',
  'owner_trust_action',
]);

/**
 * Classify a single proposal into one of the four triage buckets.
 *
 * Rules (evaluated in priority order — first match wins):
 *
 * 1. approve_today — kind is in APPROVE_TODAY_KINDS, OR tier === 1
 *    AND kind is not in BACKGROUND_HEALTH_KINDS.
 *    The tier-1 catch-all means any newly-added high-priority kind
 *    lands in approve_today automatically even if not in the set yet.
 *
 * 2. blocked — kind is in BLOCKED_KINDS.
 *
 * 3. background_health — kind is in BACKGROUND_HEALTH_KINDS.
 *
 * 4. fyi — everything else (rfq_recommend, rdo_draft, medicao_draft,
 *    procurement_action, quality_action, rnc_assign_or_escalate, etc.)
 */
export function classifyPrumoProposal(p: PrumoProposalLike): PrumoBucket {
  const kind = p.kind ?? '';

  // Rule 1 — approve_today
  if (
    APPROVE_TODAY_KINDS.has(kind) ||
    (p.tier === 1 && !BACKGROUND_HEALTH_KINDS.has(kind) && !BLOCKED_KINDS.has(kind))
  ) {
    return 'approve_today';
  }

  // Rule 2 — blocked
  if (BLOCKED_KINDS.has(kind)) {
    return 'blocked';
  }

  // Rule 3 — background_health
  if (BACKGROUND_HEALTH_KINDS.has(kind)) {
    return 'background_health';
  }

  // Rule 4 — default
  return 'fyi';
}

// ─── bucket grouping helpers ──────────────────────────────────────────────────

export const BUCKET_ORDER: PrumoBucket[] = [
  'approve_today',
  'blocked',
  'fyi',
  'background_health',
];

export interface PrumoGroup<T extends PrumoProposalLike = PrumoProposalLike> {
  id: PrumoBucket;
  items: T[];
}

/**
 * Group an array of proposals into ordered triage buckets.
 * Preserves the input order within each bucket.
 */
export function groupByBucket<T extends PrumoProposalLike>(proposals: T[]): PrumoGroup<T>[] {
  const map = new Map<PrumoBucket, T[]>(BUCKET_ORDER.map((b) => [b, []]));
  for (const p of proposals) {
    const bucket = classifyPrumoProposal(p);
    map.get(bucket)!.push(p);
  }
  return BUCKET_ORDER.map((id) => ({ id, items: map.get(id)! }));
}

/**
 * Determine which bucket should be expanded by default.
 *
 * Logic:
 *   - If approve_today has items → expand approve_today only.
 *   - Else if blocked has items → expand blocked only.
 *   - Else → expand fyi.
 *   - background_health is always collapsed by default.
 */
export function defaultExpandedBucket(groups: PrumoGroup[]): PrumoBucket {
  const find = (id: PrumoBucket) => groups.find((g) => g.id === id);
  if ((find('approve_today')?.items.length ?? 0) > 0) return 'approve_today';
  if ((find('blocked')?.items.length ?? 0) > 0) return 'blocked';
  return 'fyi';
}
